import create from 'zustand';
import produce from 'immer';

// internal
import * as ProjectAssetStore from '@app/typings/stores/initializing/project/assetDevice.d';
import * as ProjectAssetState from '@app/typings/stores/states/project/assetDevice.d';
import * as DeviceService from '@service/device';
import { handleError } from '@app/utils/service';
import {
  getProject,
  getRowsPerPage,
  getWinnonieProject,
  getWinnonieStation,
  getZone,
} from '@app/utils/localStorage';
import { notification } from 'antd';
import { ResponseMeta } from '@app/typings/services/service';

const state: ProjectAssetState.AssetDeviceState = {
  data: [],
  meta: { limit: Number(getRowsPerPage()), offset: 0, total: 0 },
  modalCreateWithProject: false,
  modalDelete: false,
  selectUpdate: '',
  selectDelete: '',
  syncDeviceLoading: false,
  loading: false,
};

export const useAssetDeviceStore = create<ProjectAssetStore.AssetDeviceStore>((set, get) => ({
  ...state,
  async fetch(zone, options) {
    try {
      if (!zone) {
        zone = getZone();
      }

      if (!zone) {
        return;
      }

      const { data } = await DeviceService.fetchWithZone(getProject(), zone, {
        offset: options?.offset,
        limit: options?.limit,
        name: options?.search,
        deviceType: options?.deviceType,
      });
      if (data.success) {
        set(
          produce<ProjectAssetState.AssetDeviceState>((state) => {
            state.data = data.data as any;
            state.meta = data.meta as ResponseMeta;
          }),
        );
      }
    } catch (error) {
      handleError(error);
    }
  },
  async fetchWithProject() {
    try {
      const { data } = await DeviceService.fetchWithProject(
        getProject() || getWinnonieProject() || getWinnonieStation(),
      );
      if (data.success) {
        set(
          produce<ProjectAssetState.AssetDeviceState>((state) => {
            state.data = data.data as any;
            state.meta = data.meta as ResponseMeta;
          }),
        );
      }
    } catch (error) {
      handleError(error);
    }
  },
  onModalCreateWithProject() {
    if (!getZone()) {
      return notification.warning({
        message: 'Alert',
        description: 'Please create zone',
      });
    }

    set(
      produce<ProjectAssetState.AssetDeviceState>((state) => {
        state.modalCreateWithProject = !state.modalCreateWithProject;
      }),
    );
  },
  async onCreateWithProject(value) {
    try {
      const { data } = await DeviceService.fetchCreate({
        zone: getZone(),
        lockers: [],
        type: value.deviceType,
        name: value.device,
      });

      if (data.success) {
        notification.success({
          message: 'Alert',
          description: `Create device name '${value.device}' success!`,
        });

        get().onModalCreateWithProject();
        get().fetch();
      }
    } catch (error: any) {
      handleError(error);
      notification.error({
        message: 'Error',
        description: error.response.data.message,
      });
    }
  },
  async onServiceDelete(deviceId) {
    try {
      await DeviceService.fetchDelete(deviceId);
      notification.success({
        message: 'Alert',
        description: 'Delete device success.',
      });

      // fetch data
      await get().fetch();
      get().onModalDelete('');
    } catch (error: any) {
      handleError(error);
      notification.error({
        message: 'Error',
        description: error.response.data.message,
      });
    }
  },
  onModalDelete(deviceId) {
    set(
      produce<ProjectAssetState.AssetDeviceState>((state) => {
        state.modalDelete = !state.modalDelete;
        state.selectDelete = deviceId;
      }),
    );
  },
  onSelectDevice(deviceId) {
    set(
      produce<ProjectAssetState.AssetDeviceState>((state) => {
        state.selectUpdate = deviceId;
      }),
    );
  },
  async onServiceFetchById(deviceId) {
    try {
      const { data } = await DeviceService.fetchById(deviceId);
      if (data.success) {
        set(
          produce<ProjectAssetState.AssetDeviceState>((state) => {
            state.deviceData = data.data as any;
          }),
        );
      }
    } catch (error) {
      handleError(error);
    }
  },
  async onServiceUpdate(id, payload) {
    try {
      const { data } = await DeviceService.fetchUpdate(id, {
        type: payload.deviceType,
        name: payload.device,
        lockers: [],
      });
      if (data.success) {
        notification.success({
          message: 'Alert',
          description: `Update this device name '${payload.device}' is success`,
        });
      }
    } catch (error: any) {
      handleError(error);
      notification.error({
        message: 'Error',
        description: error.response.data.message,
      });
    }
  },
  async onSyncDevice(deviceTypes) {
    get().onSyncDeviceLoading();
    try {
      const { data } = await DeviceService.syncDevice(getProject(), deviceTypes);
      if (data.success) {
        notification.success({
          message: 'Alert',
          description: `Sync locker device is success`,
        });
      }
    } catch (error: any) {
      handleError(error);
      notification.error({
        message: `Error locker code ${error.response.data.code}`,
        description: `${error.response.data.message}`,
      });
    }
    get().onSyncDeviceLoading();
  },
  onSyncDeviceLoading() {
    set(
      produce<ProjectAssetState.AssetDeviceState>((state) => {
        state.syncDeviceLoading = !state.syncDeviceLoading;
      }),
    );
  },
}));
